const COM_API_BASE = "https://alerta-maceio-api-production.up.railway.app/api";

const COMMUNITY_PIN_CATS = [
  { id: "reuniao", icon: "users" },
  { id: "aviso", icon: "megaphone" },
  { id: "achado_perdido", icon: "search" },
  { id: "evento", icon: "calendar" },
  { id: "ocorrencia", icon: "shield-alert" },
  { id: "outro", icon: "circle" },
];

const COMMUNITY_ICON_DEFAULT = "house";
const COMMUNITY_ICON_OPTIONS = [
  { id: "house", labelKey: "comIconHouse" },
  { id: "building-2", labelKey: "comIconBuilding" },
  { id: "briefcase", labelKey: "comIconWork" },
  { id: "signpost", labelKey: "comIconStreet" },
  { id: "store", labelKey: "comIconStore" },
  { id: "users", labelKey: "comIconGeneral" },
];

function CommunityIconPicker({ lang, value, onChange, Icon }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
      {COMMUNITY_ICON_OPTIONS.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            title={t(lang, opt.labelKey)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              width: 66, padding: "8px 4px", borderRadius: "var(--radius-md)", cursor: "pointer",
              border: active ? "1.5px solid var(--color-brand-primary)" : "1px solid var(--color-border)",
              background: active ? "var(--color-brand-tint)" : "transparent",
              fontFamily: "var(--font-body)",
            }}
          >
            <Icon name={opt.id} size={18} color={active ? "var(--color-brand-deep)" : "var(--color-text-secondary)"} />
            <span style={{ fontSize: 10, color: active ? "var(--color-brand-deep)" : "var(--color-text-muted)", textAlign: "center", lineHeight: 1.1 }}>{t(lang, opt.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

function comTimeAgo(lang, ms) {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60000);
  if (min < 1) return t(lang, "comJustNow");
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function ComPinPickerMap({ lat, lng, onPick }) {
  const mapDivRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const onPickRef = React.useRef(onPick);
  onPickRef.current = onPick;

  React.useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;
    const map = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: true, touchZoom: true }).setView([lat, lng], 15);
    L.tileLayer(window.VIGI_TILE_URL, window.VIGI_TILE_OPTIONS).addTo(map);
    const icon = L.divIcon({ className: "", html: '<div class="vigi-map-pin" style="background:var(--color-brand-primary)"></div>', iconSize: [13, 13], iconAnchor: [7, 7] });
    const marker = L.marker([lat, lng], { icon, draggable: true }).addTo(map);
    marker.on("dragend", () => { const pos = marker.getLatLng(); onPickRef.current(pos.lat, pos.lng); });
    map.on("click", (e) => { marker.setLatLng(e.latlng); onPickRef.current(e.latlng.lat, e.latlng.lng); });
    mapRef.current = map; markerRef.current = marker;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  React.useEffect(() => {
    if (!markerRef.current || !mapRef.current) return;
    const current = markerRef.current.getLatLng();
    if (Math.abs(current.lat - lat) > 1e-6 || Math.abs(current.lng - lng) > 1e-6) {
      markerRef.current.setLatLng([lat, lng]);
      mapRef.current.setView([lat, lng], 15);
    }
  }, [lat, lng]);

  return <div ref={mapDivRef} style={{ height: 140, borderRadius: 9, overflow: "hidden", position: "relative", zIndex: 0, border: "1px solid var(--color-border)" }} />;
}

const COM_CHAT_POLL_MS = 5000;

// Plain HTTP polling instead of websockets — cheap for the server (no
// persistent connections) and the "afterSeq" cursor keeps each poll
// response tiny (just new messages, never the whole history). Only polls
// while this component is mounted (chat sub-tab open) and pauses when the
// browser tab itself isn't visible.
function CommunityChat({ lang, communityId, deviceId, verifiedEmail, isAdmin }) {
  const { Icon } = window.VigiALDesignSystem_ff2dec;
  const [messages, setMessages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState("");
  const [reportedIds, setReportedIds] = React.useState(() => new Set());
  const lastSeqRef = React.useRef(0);
  const listRef = React.useRef(null);

  async function handleReportMessage(messageId) {
    setReportedIds((prev) => new Set(prev).add(messageId));
    await fetch(`${COM_API_BASE}/community-messages/${messageId}/report`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId }),
    }).catch(() => {});
  }

  async function handleDeleteMessage(messageId) {
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    await fetch(`${COM_API_BASE}/community-messages/${messageId}/delete`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId }),
    }).catch(() => {});
  }

  function mergeNew(incoming) {
    if (!incoming || !incoming.length) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      return prev.concat(incoming.filter((m) => !seen.has(m.id)));
    });
    lastSeqRef.current = incoming.reduce((max, m) => Math.max(max, m.seq), lastSeqRef.current);
  }

  React.useEffect(() => {
    setMessages([]);
    lastSeqRef.current = 0;
    setLoading(true);
    fetch(`${COM_API_BASE}/community-messages/${communityId}?deviceId=${encodeURIComponent(deviceId)}`)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setMessages(list);
        lastSeqRef.current = list.reduce((max, m) => Math.max(max, m.seq), 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    const interval = setInterval(() => {
      if (document.hidden) return;
      fetch(`${COM_API_BASE}/community-messages/${communityId}?deviceId=${encodeURIComponent(deviceId)}&afterSeq=${lastSeqRef.current}`)
        .then((r) => r.json())
        .then((data) => { if (Array.isArray(data)) mergeNew(data); })
        .catch(() => {});
    }, COM_CHAT_POLL_MS);
    return () => clearInterval(interval);
  }, [communityId]);

  React.useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  async function handleSend() {
    const value = text.trim();
    if (!value) return;
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${COM_API_BASE}/community-messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, communityId, text: value }),
      }).then((r) => r.json());
      if (res.error) { setError(res.error); setSending(false); return; }
      mergeNew([res]);
      setText("");
    } catch (e) {
      setError(t(lang, "comGenericError"));
    }
    setSending(false);
  }

  if (loading) {
    return <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)", marginBottom: 14 }}>{t(lang, "comLoading")}</div>;
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 6 }}>{t(lang, "comChatExpiryHint")}</div>
      <div ref={listRef} style={{ maxHeight: 320, overflowY: "auto", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 8 }}>
        {messages.length === 0 && (
          <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "comChatEmpty")}</div>
        )}
        {messages.map((m) => {
          const isMine = m.authorEmail === verifiedEmail;
          return (
            <div key={m.id} style={{ display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start", marginBottom: 8 }}>
              {!isMine && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 2 }}>{m.authorName || t(lang, "comAnonymousAuthor")}</div>}
              <div style={{
                maxWidth: "80%", padding: "7px 10px", borderRadius: 12,
                background: isMine ? "var(--color-brand-primary)" : "var(--color-bg)",
                border: isMine ? "none" : "1px solid var(--color-border)",
                color: isMine ? "#fff" : "var(--color-text-primary)", fontSize: "var(--text-sm)", wordBreak: "break-word",
              }}>
                {m.text}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                <span style={{ fontSize: 9, color: "var(--color-text-muted)", fontFamily: "var(--font-mono)" }}>{comTimeAgo(lang, m.createdAt)}</span>
                {!isMine && (
                  <button
                    onClick={() => handleReportMessage(m.id)}
                    disabled={reportedIds.has(m.id)}
                    style={{ background: "transparent", border: "none", cursor: reportedIds.has(m.id) ? "default" : "pointer", color: "var(--color-text-muted)", fontSize: 9, padding: 0, fontFamily: "var(--font-body)" }}
                  >
                    {reportedIds.has(m.id) ? t(lang, "comReportedMsg") : t(lang, "comReportBtn")}
                  </button>
                )}
                {(isMine || isAdmin) && (
                  <button
                    onClick={() => handleDeleteMessage(m.id)}
                    style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-muted)", fontSize: 9, padding: 0, fontFamily: "var(--font-body)" }}
                  >
                    {t(lang, "comDeletePinBtn")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {error && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 6 }}>{error}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
          placeholder={t(lang, "comChatPlaceholder")}
          maxLength={500}
          style={{ flex: 1, boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)" }}
        />
        <button onClick={handleSend} disabled={sending || !text.trim()} style={{ background: "var(--color-brand-primary)", border: "none", borderRadius: 8, padding: "0 14px", cursor: "pointer", display: "flex", alignItems: "center", opacity: sending || !text.trim() ? 0.6 : 1 }}>
          <Icon name="send" size={16} color="#fff" />
        </button>
      </div>
    </div>
  );
}

function CommunityMembers({ lang, communityId, deviceId, isAdmin, myEmail, friends }) {
  const { Icon, Button } = window.VigiALDesignSystem_ff2dec;
  const [members, setMembers] = React.useState([]);
  const [pendingRequests, setPendingRequests] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyEmail, setBusyEmail] = React.useState(null);
  const [error, setError] = React.useState("");
  const [showInvite, setShowInvite] = React.useState(false);
  const [inviteError, setInviteError] = React.useState("");

  function loadMembers() {
    setLoading(true);
    fetch(`${COM_API_BASE}/communities/${communityId}/members?deviceId=${encodeURIComponent(deviceId)}`)
      .then((r) => r.json())
      .then((data) => {
        setMembers((data && Array.isArray(data.members)) ? data.members : []);
        setPendingRequests((data && Array.isArray(data.pendingRequests)) ? data.pendingRequests : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }
  React.useEffect(loadMembers, [communityId]);

  async function act(action, targetEmail) {
    setBusyEmail(targetEmail);
    setError("");
    try {
      const res = await fetch(`${COM_API_BASE}/communities/${communityId}/${action}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, targetEmail }),
      }).then((r) => r.json());
      if (res && res.error) { setError(res.error); setBusyEmail(null); return; }
      loadMembers();
    } catch (e) {
      setError(t(lang, "comGenericError"));
    }
    setBusyEmail(null);
  }

  async function respondRequest(requesterEmail, accept) {
    setBusyEmail(requesterEmail);
    setError("");
    try {
      const res = await fetch(`${COM_API_BASE}/communities/${communityId}/join-requests/respond`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, requesterEmail, accept }),
      }).then((r) => r.json());
      if (res && res.error) { setError(res.error); setBusyEmail(null); return; }
      loadMembers();
    } catch (e) {
      setError(t(lang, "comGenericError"));
    }
    setBusyEmail(null);
  }

  async function inviteFriend(friendEmail) {
    setBusyEmail(friendEmail);
    setInviteError("");
    try {
      const res = await fetch(`${COM_API_BASE}/communities/${communityId}/invite`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, targetEmail: friendEmail }),
      }).then((r) => r.json());
      if (res && res.error) { setInviteError(res.error); setBusyEmail(null); return; }
      setShowInvite(false);
      loadMembers();
    } catch (e) {
      setInviteError(t(lang, "comGenericError"));
    }
    setBusyEmail(null);
  }

  if (loading) {
    return <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)", marginBottom: 14 }}>{t(lang, "comLoading")}</div>;
  }

  const memberEmails = new Set(members.map((m) => m.email));
  const invitableFriends = (friends || []).filter((f) => !memberEmails.has(f.email));

  return (
    <div style={{ marginBottom: 14 }}>
      {error && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 8 }}>{error}</div>}

      {isAdmin && (
        <div style={{ marginBottom: 14 }}>
          <Button variant="secondary" onClick={() => { setShowInvite((v) => !v); setInviteError(""); }} style={{ width: "100%", marginBottom: showInvite ? 8 : 0 }}>
            {t(lang, "comInviteFriendBtn")}
          </Button>
          {showInvite && (
            <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10 }}>
              {inviteError && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 8 }}>{inviteError}</div>}
              {invitableFriends.length === 0 ? (
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>{t(lang, "comNoInvitableFriends")}</div>
              ) : invitableFriends.map((f) => (
                <div key={f.email} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 0" }}>
                  <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name || f.email}</span>
                  <button
                    onClick={() => inviteFriend(f.email)}
                    disabled={busyEmail === f.email}
                    style={{ background: "transparent", border: "1px solid var(--color-brand-primary)", borderRadius: "var(--radius-pill)", padding: "3px 9px", cursor: "pointer", color: "var(--color-brand-primary)", fontSize: "var(--text-xs)", fontFamily: "var(--font-body)", flexShrink: 0 }}
                  >
                    {t(lang, "comInviteBtn")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isAdmin && pendingRequests.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--color-text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".3px" }}>
            {t(lang, "comPendingRequestsTitle")}
          </div>
          {pendingRequests.map((r) => (
            <div key={r.email} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 8 }}>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name || r.email}</span>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button onClick={() => respondRequest(r.email, false)} disabled={busyEmail === r.email} style={{ background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "4px 9px", cursor: "pointer", color: "var(--color-text-secondary)", fontSize: "var(--text-xs)", fontFamily: "var(--font-body)" }}>
                  {t(lang, "comDeclineBtn")}
                </button>
                <button onClick={() => respondRequest(r.email, true)} disabled={busyEmail === r.email} style={{ background: "var(--color-brand-primary)", border: "none", borderRadius: "var(--radius-pill)", padding: "4px 9px", cursor: "pointer", color: "var(--color-bg)", fontSize: "var(--text-xs)", fontWeight: 600, fontFamily: "var(--font-body)" }}>
                  {t(lang, "comAcceptBtn")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {members.map((m) => {
        const canManage = isAdmin && m.email !== myEmail && !m.isCreator;
        return (
          <div key={m.email} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              {window.AvatarBadge ? <window.AvatarBadge avatar={m.avatar} size={32} /> : (
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--color-brand-tint)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="user" size={15} color="var(--color-brand-deep)" />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.name || m.email}
                </div>
                {(m.isCreator || m.isAdmin) && (
                  <div style={{ fontSize: 10, color: "var(--color-brand-primary)", fontWeight: 600 }}>
                    {t(lang, m.isCreator ? "comCreatorBadge" : "comAdminBadge")}
                  </div>
                )}
              </div>
            </div>
            {canManage && (
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                  onClick={() => act(m.isAdmin ? "demote" : "promote", m.email)}
                  disabled={busyEmail === m.email}
                  style={{ background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "4px 9px", cursor: "pointer", color: "var(--color-text-secondary)", fontSize: "var(--text-xs)", fontFamily: "var(--font-body)" }}
                >
                  {t(lang, m.isAdmin ? "comDemoteBtn" : "comPromoteBtn")}
                </button>
                <button
                  onClick={() => act("remove-member", m.email)}
                  disabled={busyEmail === m.email}
                  style={{ background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "4px 9px", cursor: "pointer", color: "var(--color-alert)", fontSize: "var(--text-xs)", fontFamily: "var(--font-body)" }}
                >
                  {t(lang, "comRemoveMemberBtn")}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ComunidadesScreen({ lang, deepLink }) {
  const { Button, Icon } = window.VigiALDesignSystem_ff2dec;
  const deviceId = window.getDeviceId ? window.getDeviceId() : null;
  const premium = window.isPremium ? window.isPremium() : false;

  const [verifiedEmail, setVerifiedEmail] = React.useState(undefined); // undefined = loading, null = not verified
  const [communities, setCommunities] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [detailTab, setDetailTab] = React.useState("mural");
  const [pins, setPins] = React.useState([]);
  const [pinsLoading, setPinsLoading] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [reportedPinIds, setReportedPinIds] = React.useState(() => new Set());

  const [showCreate, setShowCreate] = React.useState(false);
  const [showCreatePremiumHint, setShowCreatePremiumHint] = React.useState(false);
  const [createName, setCreateName] = React.useState("");
  const [createDesc, setCreateDesc] = React.useState("");
  const [createIcon, setCreateIcon] = React.useState(COMMUNITY_ICON_DEFAULT);
  const [createSaving, setCreateSaving] = React.useState(false);
  const [createdCode, setCreatedCode] = React.useState(null);
  const [codeCopied, setCodeCopied] = React.useState(false);

  const [confirmLeave, setConfirmLeave] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [deleteSaving, setDeleteSaving] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");
  const [showEditCommunity, setShowEditCommunity] = React.useState(false);
  const [editName, setEditName] = React.useState("");
  const [editDesc, setEditDesc] = React.useState("");
  const [editIcon, setEditIcon] = React.useState(COMMUNITY_ICON_DEFAULT);
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState("");

  const [showJoin, setShowJoin] = React.useState(false);
  const [joinCode, setJoinCode] = React.useState("");
  const [joinSaving, setJoinSaving] = React.useState(false);
  const [joinError, setJoinError] = React.useState("");
  const [joinPendingMsg, setJoinPendingMsg] = React.useState(null);

  const [showNewPin, setShowNewPin] = React.useState(false);
  const [pinCategory, setPinCategory] = React.useState("aviso");
  const [pinTitle, setPinTitle] = React.useState("");
  const [pinDesc, setPinDesc] = React.useState("");
  const [pinLat, setPinLat] = React.useState(-9.6658);
  const [pinLng, setPinLng] = React.useState(-35.7353);
  const [pinSaving, setPinSaving] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState(deepLink && deepLink.tab === "amigos" ? "amigos" : "comunidades");
  const [friendCode, setFriendCode] = React.useState(null);
  const [friends, setFriends] = React.useState([]);
  const [requestsIncoming, setRequestsIncoming] = React.useState([]);
  const [requestsOutgoing, setRequestsOutgoing] = React.useState([]);
  const [friendsLoading, setFriendsLoading] = React.useState(true);
  const [friendCodeCopied, setFriendCodeCopied] = React.useState(false);
  const [addFriendCode, setAddFriendCode] = React.useState("");
  const [addFriendSaving, setAddFriendSaving] = React.useState(false);
  const [addFriendError, setAddFriendError] = React.useState("");
  const [addFriendSuccess, setAddFriendSuccess] = React.useState("");
  const [respondingEmail, setRespondingEmail] = React.useState(null);

  function loadFriends() {
    if (!deviceId) return;
    setFriendsLoading(true);
    fetch(`${COM_API_BASE}/friends/mine`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.linked) {
          setFriendCode(data.friendCode);
          setFriends(data.friends || []);
          setRequestsIncoming(data.requestsIncoming || []);
          setRequestsOutgoing(data.requestsOutgoing || []);
        }
        setFriendsLoading(false);
      })
      .catch(() => setFriendsLoading(false));
  }

  async function handleAddFriend() {
    const codeOrUsername = addFriendCode.trim();
    if (!codeOrUsername) return;
    setAddFriendSaving(true);
    setAddFriendError("");
    setAddFriendSuccess("");
    try {
      const res = await fetch(`${COM_API_BASE}/friends/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, codeOrUsername }),
      }).then((r) => r.json());
      if (res.error) { setAddFriendError(res.error); setAddFriendSaving(false); return; }
      setAddFriendCode("");
      setAddFriendSuccess(res.status === "accepted" ? t(lang, "comFriendAcceptedMsg") : t(lang, "comFriendRequestSentMsg"));
      loadFriends();
    } catch (e) {
      setAddFriendError(t(lang, "comGenericError"));
    }
    setAddFriendSaving(false);
  }

  async function handleRespondFriend(requesterEmail, accept) {
    setRespondingEmail(requesterEmail);
    try {
      await fetch(`${COM_API_BASE}/friends/respond`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, requesterEmail, accept }),
      }).catch(() => {});
      loadFriends();
    } finally {
      setRespondingEmail(null);
    }
  }

  async function handleRemoveFriend(friendEmail) {
    await fetch(`${COM_API_BASE}/friends/remove`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId, friendEmail }),
    }).catch(() => {});
    loadFriends();
  }

  function copyFriendCode() {
    if (!friendCode) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(friendCode).then(() => { setFriendCodeCopied(true); setTimeout(() => setFriendCodeCopied(false), 2000); }).catch(() => {});
    }
  }

  function loadCommunities() {
    if (!deviceId) return;
    setLoading(true);
    fetch(`${COM_API_BASE}/communities/mine`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId }),
    })
      .then((r) => r.json())
      .then((data) => { setCommunities(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  React.useEffect(() => {
    if (!deviceId) return;
    fetch(`${COM_API_BASE}/device-trust/mine`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ deviceId }),
    })
      .then((r) => r.json())
      .then((d) => setVerifiedEmail(d.verifiedEmail || null))
      .catch(() => setVerifiedEmail(null));
    loadCommunities();
    loadFriends();
  }, []);

  function loadPins(communityId) {
    setPinsLoading(true);
    fetch(`${COM_API_BASE}/community-pins/${communityId}?deviceId=${encodeURIComponent(deviceId)}`)
      .then((r) => r.json())
      .then((data) => { setPins(Array.isArray(data) ? data : []); setPinsLoading(false); })
      .catch(() => setPinsLoading(false));
  }

  function openCommunity(c) {
    setSelected(c);
    setShowNewPin(false);
    setDetailTab("mural");
    setConfirmLeave(false);
    setConfirmDelete(false);
    setDeleteError("");
    setShowEditCommunity(false);
    loadPins(c.id);
  }

  function backToList() {
    setSelected(null);
    setPins([]);
    loadCommunities();
  }

  React.useEffect(() => {
    if (!deepLink || !deepLink.communityId || selected || communities.length === 0) return;
    const match = communities.find((c) => c.id === deepLink.communityId);
    if (match) {
      openCommunity(match);
      if (deepLink.detailTab) setDetailTab(deepLink.detailTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, communities]);

  async function handleCreate() {
    const name = createName.trim();
    if (!name) return;
    setCreateSaving(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${COM_API_BASE}/communities`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, name, description: createDesc.trim(), icon: createIcon }),
      }).then((r) => r.json());
      if (res.error) { setErrorMsg(res.error); setCreateSaving(false); return; }
      setCreatedCode(res.inviteCode);
      setCreateName(""); setCreateDesc(""); setCreateIcon(COMMUNITY_ICON_DEFAULT);
      loadCommunities();
    } catch (e) {
      setErrorMsg(t(lang, "comGenericError"));
    }
    setCreateSaving(false);
  }

  function openEditCommunity() {
    if (!selected) return;
    setEditName(selected.name);
    setEditDesc(selected.description || "");
    setEditIcon(selected.icon || COMMUNITY_ICON_DEFAULT);
    setEditError("");
    setShowEditCommunity(true);
  }

  async function handleEditCommunity() {
    const name = editName.trim();
    if (!name || !selected) return;
    setEditSaving(true);
    setEditError("");
    try {
      const res = await fetch(`${COM_API_BASE}/communities/${selected.id}/update`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, name, description: editDesc.trim(), icon: editIcon }),
      }).then((r) => r.json());
      if (res.error) { setEditError(res.error); setEditSaving(false); return; }
      setSelected(res);
      setShowEditCommunity(false);
      loadCommunities();
    } catch (e) {
      setEditError(t(lang, "comGenericError"));
    }
    setEditSaving(false);
  }

  function closeCreateFlow() {
    setShowCreate(false);
    setCreatedCode(null);
    setCodeCopied(false);
  }

  function copyCode(code) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }).catch(() => {});
    }
  }

  async function handleJoin() {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setJoinSaving(true);
    setJoinError("");
    try {
      const res = await fetch(`${COM_API_BASE}/communities/join`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, inviteCode: code }),
      }).then((r) => r.json());
      if (res.error) { setJoinError(res.error); setJoinSaving(false); return; }
      setJoinCode("");
      if (res.pending) {
        setJoinPendingMsg(res.communityName);
      } else {
        setShowJoin(false);
        loadCommunities();
      }
      if (window.trackEvent) window.trackEvent("community_join");
    } catch (e) {
      setJoinError(t(lang, "comGenericError"));
    }
    setJoinSaving(false);
  }

  async function toggleMuteCommunity() {
    if (!selected) return;
    const nextMuted = !selected.muted;
    setSelected({ ...selected, muted: nextMuted });
    try {
      await fetch(`${COM_API_BASE}/communities/${selected.id}/mute`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId, muted: nextMuted }),
      });
    } catch (e) {}
  }

  async function handleLeave() {
    if (!selected) return;
    await fetch(`${COM_API_BASE}/communities/${selected.id}/leave`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId }),
    }).catch(() => {});
    setConfirmLeave(false);
    backToList();
  }

  async function handleDeleteCommunity() {
    if (!selected) return;
    setDeleteSaving(true);
    setDeleteError("");
    try {
      const res = await fetch(`${COM_API_BASE}/communities/${selected.id}/delete`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceId }),
      }).then((r) => r.json());
      if (res.error) { setDeleteError(res.error); setDeleteSaving(false); return; }
      setConfirmDelete(false);
      backToList();
    } catch (e) {
      setDeleteError(t(lang, "comGenericError"));
    }
    setDeleteSaving(false);
  }

  function openNewPinForm() {
    setShowNewPin(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { setPinLat(pos.coords.latitude); setPinLng(pos.coords.longitude); },
        () => {},
        { timeout: 3000 }
      );
    }
  }

  async function handleCreatePin() {
    const title = pinTitle.trim();
    if (!title || !selected) return;
    setPinSaving(true);
    try {
      const res = await fetch(`${COM_API_BASE}/community-pins`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deviceId, communityId: selected.id, category: pinCategory,
          title, description: pinDesc.trim(), lat: pinLat, lng: pinLng,
        }),
      }).then((r) => r.json());
      if (res.error) { setErrorMsg(res.error); setPinSaving(false); return; }
      setPinTitle(""); setPinDesc(""); setShowNewPin(false);
      loadPins(selected.id);
    } catch (e) {
      setErrorMsg(t(lang, "comGenericError"));
    }
    setPinSaving(false);
  }

  async function handleDeletePin(pinId) {
    await fetch(`${COM_API_BASE}/community-pins/${pinId}/delete`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId }),
    }).catch(() => {});
    if (selected) loadPins(selected.id);
  }

  async function handleReportPin(pinId) {
    setReportedPinIds((prev) => new Set(prev).add(pinId));
    await fetch(`${COM_API_BASE}/community-pins/${pinId}/report`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ deviceId }),
    }).catch(() => {});
  }

  if (verifiedEmail === undefined || loading) {
    return <div style={{ padding: 14, fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "comLoading")}</div>;
  }

  if (!verifiedEmail) {
    return (
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>{t(lang, "comNeedsVerifiedEmail")}</div>
      </div>
    );
  }

  // --- Community detail view ---
  if (selected) {
    return (
      <div style={{ padding: 14, flex: 1, overflowY: "auto" }}>
        <button onClick={backToList} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)", fontSize: "var(--text-xs)", padding: 0, marginBottom: 10, fontFamily: "var(--font-body)" }}>
          <Icon name="chevron-left" size={14} /> {t(lang, "comBackToList")}
        </button>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: "var(--radius-md)", background: "var(--color-brand-tint)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name={selected.icon || COMMUNITY_ICON_DEFAULT} size={17} color="var(--color-brand-deep)" />
            </div>
            <div style={{ fontSize: "var(--text-lg)", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected.name}</div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={toggleMuteCommunity} title={t(lang, selected.muted ? "comUnmuteBtn" : "comMuteBtn")} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "5px 11px", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "var(--text-xs)", color: "var(--color-text-secondary)" }}>
              <Icon name={selected.muted ? "bell-off" : "bell"} size={12} />
            </button>
            {selected.isAdmin && (
              <button onClick={openEditCommunity} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "5px 11px", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "var(--text-xs)", color: "var(--color-text-secondary)" }}>
                <Icon name="pencil" size={12} /> {t(lang, "comEditBtn")}
              </button>
            )}
            {!selected.isCreator && (
              <button onClick={() => setConfirmLeave(true)} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "5px 11px", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "var(--text-xs)", color: "var(--color-alert)" }}>
                <Icon name="log-out" size={12} color="var(--color-alert)" /> {t(lang, "comLeaveBtn")}
              </button>
            )}
            {selected.isCreator && (
              <button onClick={() => setConfirmDelete(true)} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "5px 11px", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "var(--text-xs)", color: "var(--color-alert)" }}>
                <Icon name="trash-2" size={12} color="var(--color-alert)" /> {t(lang, "comDeleteCommunityBtn")}
              </button>
            )}
          </div>
        </div>

        {confirmLeave && (
          <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)", marginBottom: 10 }}>{t(lang, "comLeaveConfirmMsg")}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={() => setConfirmLeave(false)} style={{ flex: 1 }}>{t(lang, "comCancelBtn")}</Button>
              <Button variant="primary" onClick={handleLeave} style={{ flex: 1, background: "var(--color-alert)", borderColor: "var(--color-alert)" }}>{t(lang, "comLeaveBtn")}</Button>
            </div>
          </div>
        )}

        {confirmDelete && (
          <div style={{ border: "1px solid var(--color-alert)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)", marginBottom: 10 }}>{t(lang, "comDeleteConfirmMsg")}</div>
            {deleteError && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 8 }}>{deleteError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={() => setConfirmDelete(false)} style={{ flex: 1 }}>{t(lang, "comCancelBtn")}</Button>
              <Button variant="primary" onClick={handleDeleteCommunity} disabled={deleteSaving} style={{ flex: 1, background: "var(--color-alert)", borderColor: "var(--color-alert)" }}>{t(lang, "comDeleteCommunityBtn")}</Button>
            </div>
          </div>
        )}
        {selected.description && <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: 8, marginTop: 4 }}>{selected.description}</div>}

        {showEditCommunity && (
          <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={t(lang, "comNamePlaceholder")}
              maxLength={60}
              style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8 }}
            />
            <textarea
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              placeholder={t(lang, "comDescPlaceholder")}
              maxLength={200}
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8, resize: "vertical" }}
            />
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>{t(lang, "comIconPickerLabel")}</div>
            <CommunityIconPicker lang={lang} value={editIcon} onChange={setEditIcon} Icon={Icon} />
            {editError && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 8 }}>{editError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={() => setShowEditCommunity(false)} style={{ flex: 1 }}>{t(lang, "comCancelBtn")}</Button>
              <Button variant="primary" onClick={handleEditCommunity} disabled={editSaving || !editName.trim()} style={{ flex: 1 }}>{t(lang, "comSaveBtn")}</Button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <button onClick={() => setDetailTab("membros")} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font-body)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)", textDecoration: "underline" }}>
            {selected.memberCount} {t(lang, selected.memberCount === 1 ? "comMemberSingular" : "comMemberPlural")}
          </button>
          <button onClick={() => copyCode(selected.inviteCode)} style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "2px 9px", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--color-text-secondary)" }}>
            <Icon name="copy" size={11} /> {selected.inviteCode}
          </button>
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 14, border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: 3 }}>
          <button
            onClick={() => setDetailTab("mural")}
            style={{
              flex: 1, padding: "6px 0", borderRadius: "var(--radius-pill)", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
              fontSize: "var(--text-sm)", fontWeight: 600,
              background: detailTab === "mural" ? "var(--color-brand-tint)" : "transparent",
              color: detailTab === "mural" ? "var(--color-brand-deep)" : "var(--color-text-secondary)",
            }}
          >
            {t(lang, "comMuralTab")}
          </button>
          <button
            onClick={() => setDetailTab("chat")}
            style={{
              flex: 1, padding: "6px 0", borderRadius: "var(--radius-pill)", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
              fontSize: "var(--text-sm)", fontWeight: 600,
              background: detailTab === "chat" ? "var(--color-brand-tint)" : "transparent",
              color: detailTab === "chat" ? "var(--color-brand-deep)" : "var(--color-text-secondary)",
            }}
          >
            {t(lang, "comChatTab")}
          </button>
          <button
            onClick={() => setDetailTab("membros")}
            style={{
              flex: 1, padding: "6px 0", borderRadius: "var(--radius-pill)", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
              fontSize: "var(--text-sm)", fontWeight: 600,
              background: detailTab === "membros" ? "var(--color-brand-tint)" : "transparent",
              color: detailTab === "membros" ? "var(--color-brand-deep)" : "var(--color-text-secondary)",
            }}
          >
            {t(lang, "comMembersTab")}
          </button>
        </div>

        {detailTab === "membros" && (
          <CommunityMembers lang={lang} communityId={selected.id} deviceId={deviceId} isAdmin={selected.isAdmin} myEmail={verifiedEmail} friends={friends} />
        )}

        {detailTab === "chat" && (
          <CommunityChat lang={lang} communityId={selected.id} deviceId={deviceId} verifiedEmail={verifiedEmail} isAdmin={selected.isAdmin} />
        )}

        {detailTab === "mural" && (
        <React.Fragment>
        {errorMsg && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 8 }}>{errorMsg}</div>}

        {!showNewPin ? (
          <Button variant="primary" onClick={openNewPinForm} style={{ width: "100%", marginBottom: 14 }}>{t(lang, "comNewPinBtn")}</Button>
        ) : (
          <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)", fontWeight: 600, marginBottom: 8 }}>{t(lang, "comNewPinBtn")}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
              {COMMUNITY_PIN_CATS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setPinCategory(c.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 4, padding: "5px 9px", borderRadius: "var(--radius-pill)",
                    border: pinCategory === c.id ? "1px solid var(--color-brand-primary)" : "1px solid var(--color-border)",
                    background: pinCategory === c.id ? "var(--color-brand-tint)" : "transparent",
                    color: pinCategory === c.id ? "var(--color-brand-deep)" : "var(--color-text-secondary)",
                    cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "var(--text-xs)",
                  }}
                >
                  <Icon name={c.icon} size={12} /> {t(lang, `comCat_${c.id}`)}
                </button>
              ))}
            </div>
            <input
              value={pinTitle}
              onChange={(e) => setPinTitle(e.target.value)}
              placeholder={t(lang, "comPinTitlePlaceholder")}
              maxLength={80}
              style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8 }}
            />
            <textarea
              value={pinDesc}
              onChange={(e) => setPinDesc(e.target.value)}
              placeholder={t(lang, "comPinDescPlaceholder")}
              maxLength={280}
              rows={3}
              style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8, resize: "vertical" }}
            />
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>{t(lang, "comPinLocationHint")}</div>
            <ComPinPickerMap lat={pinLat} lng={pinLng} onPick={(lat, lng) => { setPinLat(lat); setPinLng(lng); }} />
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <Button variant="secondary" onClick={() => setShowNewPin(false)} style={{ flex: 1 }}>{t(lang, "comCancelBtn")}</Button>
              <Button variant="primary" onClick={handleCreatePin} disabled={pinSaving || !pinTitle.trim()} style={{ flex: 1 }}>{t(lang, "comPostBtn")}</Button>
            </div>
          </div>
        )}

        {pinsLoading && <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "comLoading")}</div>}
        {!pinsLoading && pins.length === 0 && (
          <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)", marginBottom: 14 }}>{t(lang, "comNoPinsYet")}</div>
        )}
        {pins.map((p) => {
          const catDef = COMMUNITY_PIN_CATS.find((c) => c.id === p.category) || COMMUNITY_PIN_CATS.find((c) => c.id === "outro");
          const isMine = p.authorEmail === verifiedEmail;
          const canDelete = isMine || selected.isAdmin;
          return (
            <div key={p.id} style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <Icon name={catDef.icon} size={14} color="var(--color-brand-primary)" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--color-text-primary)" }}>{p.title}</span>
                </div>
                <span style={{ fontSize: 10, color: "var(--color-text-muted)", flexShrink: 0, fontFamily: "var(--font-mono)" }}>{comTimeAgo(lang, p.createdAt)}</span>
              </div>
              {p.description && <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginTop: 4 }}>{p.description}</div>}
              {p.sourceUrl && (
                <a href={p.sourceUrl} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, fontSize: "var(--text-xs)", color: "var(--color-brand-primary)" }}>
                  <Icon name="external-link" size={11} color="var(--color-brand-primary)" /> {t(lang, "comOpenSourceLink")}
                </a>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
                <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{p.authorName || t(lang, "comAnonymousAuthor")}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {!isMine && (
                    <button
                      onClick={() => handleReportPin(p.id)}
                      disabled={reportedPinIds.has(p.id)}
                      style={{ background: "transparent", border: "none", cursor: reportedPinIds.has(p.id) ? "default" : "pointer", color: "var(--color-text-muted)", fontSize: 10, padding: 0, fontFamily: "var(--font-body)" }}
                    >
                      {reportedPinIds.has(p.id) ? t(lang, "comReportedMsg") : t(lang, "comReportBtn")}
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => handleDeletePin(p.id)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-muted)", fontSize: 10, padding: 0, fontFamily: "var(--font-body)" }}>
                      {t(lang, "comDeletePinBtn")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        </React.Fragment>
        )}
      </div>
    );
  }

  // --- List view ---
  return (
    <div style={{ padding: 14, flex: 1, overflowY: "auto" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: 3 }}>
        <button
          onClick={() => setActiveTab("comunidades")}
          style={{
            flex: 1, padding: "6px 0", borderRadius: "var(--radius-pill)", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
            fontSize: "var(--text-sm)", fontWeight: 600,
            background: activeTab === "comunidades" ? "var(--color-brand-tint)" : "transparent",
            color: activeTab === "comunidades" ? "var(--color-brand-deep)" : "var(--color-text-secondary)",
          }}
        >
          {t(lang, "tabComunidades")}
        </button>
        <button
          onClick={() => setActiveTab("amigos")}
          style={{
            flex: 1, padding: "6px 0", borderRadius: "var(--radius-pill)", border: "none", cursor: "pointer", fontFamily: "var(--font-body)",
            fontSize: "var(--text-sm)", fontWeight: 600,
            background: activeTab === "amigos" ? "var(--color-brand-tint)" : "transparent",
            color: activeTab === "amigos" ? "var(--color-brand-deep)" : "var(--color-text-secondary)",
          }}
        >
          {t(lang, "comAmigosTab")}
        </button>
      </div>

      {activeTab === "amigos" && (
        <React.Fragment>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 12 }}>{t(lang, "comAmigosIntro")}</div>

          <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>{t(lang, "comMyCodeLabel")}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "8px 0", marginBottom: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: 3, fontFamily: "var(--font-mono)", color: "var(--color-brand-primary)" }}>{friendCode || "······"}</span>
            </div>
            <Button variant="secondary" onClick={copyFriendCode} disabled={!friendCode} style={{ width: "100%" }}>
              {friendCodeCopied ? t(lang, "pixCopiedMsg") : t(lang, "comCopyCodeBtn")}
            </Button>
          </div>

          <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>{t(lang, "comAddFriendLabel")}</div>
            <input
              value={addFriendCode}
              onChange={(e) => { setAddFriendCode(e.target.value); setAddFriendError(""); }}
              placeholder={t(lang, "comCodeOrUsernamePlaceholder")}
              maxLength={40}
              style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-mono)", letterSpacing: 1, marginBottom: 8 }}
            />
            {addFriendError && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 8 }}>{addFriendError}</div>}
            {addFriendSuccess && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", marginBottom: 8 }}>{addFriendSuccess}</div>}
            <Button variant="primary" onClick={handleAddFriend} disabled={addFriendSaving || !addFriendCode.trim()} style={{ width: "100%" }}>{t(lang, "comAddFriendBtn")}</Button>
          </div>

          {!friendsLoading && requestsIncoming.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>{t(lang, "comRequestsIncomingLabel")}</div>
              {requestsIncoming.map((f) => (
                <div key={f.email} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    {window.AvatarBadge ? <window.AvatarBadge avatar={f.avatar} size={32} /> : (
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--color-brand-tint)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="user" size={15} color="var(--color-brand-deep)" />
                      </div>
                    )}
                    <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.name || f.email}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => handleRespondFriend(f.email, false)} disabled={respondingEmail === f.email} style={{ background: "transparent", border: "1px solid var(--color-border)", borderRadius: "var(--radius-pill)", padding: "4px 10px", cursor: "pointer", color: "var(--color-text-secondary)", fontSize: "var(--text-xs)", fontFamily: "var(--font-body)" }}>
                      {t(lang, "comDeclineBtn")}
                    </button>
                    <button onClick={() => handleRespondFriend(f.email, true)} disabled={respondingEmail === f.email} style={{ background: "var(--color-brand-primary)", border: "none", borderRadius: "var(--radius-pill)", padding: "4px 10px", cursor: "pointer", color: "var(--color-bg)", fontSize: "var(--text-xs)", fontWeight: 600, fontFamily: "var(--font-body)" }}>
                      {t(lang, "comAcceptBtn")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!friendsLoading && requestsOutgoing.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>{t(lang, "comRequestsOutgoingLabel")}</div>
              {requestsOutgoing.map((f) => (
                <div key={f.email} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 8, opacity: 0.7 }}>
                  {window.AvatarBadge ? <window.AvatarBadge avatar={f.avatar} size={32} /> : (
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--color-brand-tint)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon name="user" size={15} color="var(--color-brand-deep)" />
                    </div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.name || f.email}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t(lang, "comRequestPendingHint")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {friendsLoading && <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "comLoading")}</div>}
          {!friendsLoading && friends.length === 0 && (
            <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "comNoFriendsYet")}</div>
          )}
          {friends.map((f) => (
            <div key={f.email} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 10, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                {window.AvatarBadge ? <window.AvatarBadge avatar={f.avatar} size={32} /> : (
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--color-brand-tint)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name="user" size={15} color="var(--color-brand-deep)" />
                  </div>
                )}
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.name || f.email}
                </div>
              </div>
              <button onClick={() => handleRemoveFriend(f.email)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-muted)", fontSize: "var(--text-xs)", fontFamily: "var(--font-body)", flexShrink: 0 }}>
                {t(lang, "comRemoveFriendBtn")}
              </button>
            </div>
          ))}
        </React.Fragment>
      )}

      {activeTab === "comunidades" && (
      <React.Fragment>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 12 }}>{t(lang, "comIntro")}</div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <Button
          variant="primary"
          onClick={() => {
            if (!premium) { setShowCreatePremiumHint(true); setShowJoin(false); setShowCreate(false); return; }
            setShowCreate(true); setShowJoin(false); setShowCreatePremiumHint(false);
          }}
          style={{ flex: 1 }}
        >
          {t(lang, "comCreateBtn")}
        </Button>
        <Button variant="secondary" onClick={() => { setShowJoin(true); setShowCreate(false); setShowCreatePremiumHint(false); }} style={{ flex: 1 }}>{t(lang, "comJoinBtn")}</Button>
      </div>

      {showCreatePremiumHint && (
        <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Icon name="crown" size={16} color="#D4AF37" />
            <span style={{ fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--color-text-primary)" }}>{t(lang, "comPremiumGateTitle")}</span>
          </div>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)" }}>{t(lang, "comPremiumGateBody")}</div>
        </div>
      )}

      {showCreate && (
        <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
          {createdCode ? (
            <React.Fragment>
              <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)", marginBottom: 8 }}>{t(lang, "comCreatedSuccess")}</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 0", marginBottom: 10 }}>
                <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: 3, fontFamily: "var(--font-mono)", color: "var(--color-brand-primary)" }}>{createdCode}</span>
              </div>
              <Button variant="secondary" onClick={() => copyCode(createdCode)} style={{ width: "100%", marginBottom: 8 }}>
                {codeCopied ? t(lang, "pixCopiedMsg") : t(lang, "comCopyCodeBtn")}
              </Button>
              <Button variant="primary" onClick={closeCreateFlow} style={{ width: "100%" }}>{t(lang, "comDoneBtn")}</Button>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder={t(lang, "comNamePlaceholder")}
                maxLength={60}
                style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8 }}
              />
              <textarea
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                placeholder={t(lang, "comDescPlaceholder")}
                maxLength={200}
                rows={2}
                style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-body)", marginBottom: 8, resize: "vertical" }}
              />
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>{t(lang, "comIconPickerLabel")}</div>
              <CommunityIconPicker lang={lang} value={createIcon} onChange={setCreateIcon} Icon={Icon} />
              {errorMsg && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 8 }}>{errorMsg}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="secondary" onClick={() => setShowCreate(false)} style={{ flex: 1 }}>{t(lang, "comCancelBtn")}</Button>
                <Button variant="primary" onClick={handleCreate} disabled={createSaving || !createName.trim()} style={{ flex: 1 }}>{t(lang, "comCreateConfirmBtn")}</Button>
              </div>
            </React.Fragment>
          )}
        </div>
      )}

      {showJoin && (
        <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 14 }}>
          {joinPendingMsg ? (
            <React.Fragment>
              <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: 10 }}>
                {t(lang, "comJoinRequestSentMsg")} <strong>{joinPendingMsg}</strong>.
              </div>
              <Button variant="secondary" onClick={() => { setShowJoin(false); setJoinPendingMsg(null); }} style={{ width: "100%" }}>{t(lang, "comCancelBtn")}</Button>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <input
                value={joinCode}
                onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setJoinError(""); }}
                placeholder={t(lang, "comCodePlaceholder")}
                maxLength={6}
                style={{ width: "100%", boxSizing: "border-box", fontSize: "var(--text-sm)", padding: 9, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", color: "var(--color-text-primary)", fontFamily: "var(--font-mono)", letterSpacing: 2, marginBottom: 8 }}
              />
              {joinError && <div style={{ fontSize: "var(--text-xs)", color: "var(--color-alert)", marginBottom: 8 }}>{joinError}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="secondary" onClick={() => setShowJoin(false)} style={{ flex: 1 }}>{t(lang, "comCancelBtn")}</Button>
                <Button variant="primary" onClick={handleJoin} disabled={joinSaving || !joinCode.trim()} style={{ flex: 1 }}>{t(lang, "comJoinConfirmBtn")}</Button>
              </div>
            </React.Fragment>
          )}
        </div>
      )}

      {communities.length === 0 && !showCreate && !showJoin && (
        <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{t(lang, "comNoCommunitiesYet")}</div>
      )}

      {communities.map((c) => (
        <button
          key={c.id}
          onClick={() => openCommunity(c)}
          style={{
            width: "100%", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between",
            border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 12, marginBottom: 8,
            background: "transparent", cursor: "pointer", fontFamily: "var(--font-body)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: "var(--radius-md)", background: "var(--color-brand-tint)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name={c.icon || COMMUNITY_ICON_DEFAULT} size={18} color="var(--color-brand-deep)" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</div>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                {c.memberCount} {t(lang, c.memberCount === 1 ? "comMemberSingular" : "comMemberPlural")}
                {c.isCreator ? ` · ${t(lang, "comCreatorBadge")}` : c.isAdmin ? ` · ${t(lang, "comAdminBadge")}` : ""}
              </div>
            </div>
          </div>
          <Icon name="chevron-right" size={16} color="var(--color-text-muted)" style={{ flexShrink: 0 }} />
        </button>
      ))}
      </React.Fragment>
      )}
    </div>
  );
}
window.ComunidadesScreen = ComunidadesScreen;
